import { useEffect, useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import {
  ArrowUpRight, UserPlus, PhoneCall, CalendarCheck, Search, MousePointerClick,
} from "lucide-react";
import { MaskedLine, Eyebrow } from "./Reveal";
import { scrollToId, goToAudit } from "@/lib/scroll";

const NOTIFICATIONS = [
  { icon: UserPlus, title: "New Lead", sub: 'Sarah M. — "Requesting a quote"' },
  { icon: PhoneCall, title: "Incoming Call", sub: "New customer calling" },
  { icon: CalendarCheck, title: "Appointment Request", sub: "Tomorrow • 2:30 PM" },
  { icon: UserPlus, title: "New Inquiry", sub: "From your Google ad" },
];

const PhoneCard = () => {
  const [idx, setIdx] = useState(0);

  useEffect(() => {
    const t = setInterval(() => setIdx((i) => (i + 1) % NOTIFICATIONS.length), 2800);
    return () => clearInterval(t);
  }, []);

  return (
    <div
      data-testid="hero-phone-card"
      className="relative w-full max-w-[320px] rounded-[2rem] border border-white/10 bg-[#101015] p-4 shadow-2xl shadow-black/60"
    >
      <div className="flex items-center justify-between px-2 pt-1">
        <p className="text-[10px] font-bold uppercase tracking-[0.2em] text-zinc-500">Your Business</p>
        <span className="flex items-center gap-1.5 text-[10px] font-bold text-zinc-500">
          <span className="h-1.5 w-1.5 rounded-full bg-emerald-400" /> Ads running
        </span>
      </div>
      <div className="mt-4 space-y-3">
        {[0, 1].map((slot) => {
          const n = NOTIFICATIONS[(idx + slot) % NOTIFICATIONS.length];
          return (
            <AnimatePresence mode="popLayout" key={slot}>
              <motion.div
                key={`${slot}-${(idx + slot) % NOTIFICATIONS.length}`}
                initial={{ opacity: 0, y: 14 }}
                animate={{ opacity: slot === 0 ? 1 : 0.55, y: 0 }}
                exit={{ opacity: 0, y: -10 }}
                transition={{ duration: 0.45, ease: [0.16, 1, 0.3, 1] }}
                className="flex items-center gap-3 rounded-2xl border border-white/10 bg-[#17171d] p-4"
              >
                <span className="flex h-10 w-10 shrink-0 items-center justify-center rounded-full bg-purple-600">
                  <n.icon className="h-5 w-5 text-white" />
                </span>
                <div className="min-w-0">
                  <p className="text-sm font-bold text-white">{n.title}</p>
                  <p className="truncate text-xs text-zinc-400">{n.sub}</p>
                </div>
              </motion.div>
            </AnimatePresence>
          );
        })}
      </div>
      <p className="px-2 pb-1 pt-4 text-center text-[10px] font-bold uppercase tracking-[0.2em] text-zinc-600">
        Leads coming in while you work
      </p>
    </div>
  );
};

const MetaAdCard = () => (
  <div
    data-testid="hero-meta-ad-card"
    className="w-[220px] overflow-hidden rounded-2xl border border-white/10 bg-[#101015] shadow-xl shadow-black/50"
  >
    <div className="flex items-center gap-2 px-3.5 py-2.5">
      <span className="h-6 w-6 rounded-full bg-gradient-to-br from-purple-500 to-purple-700" />
      <div>
        <p className="text-[11px] font-bold leading-none text-white">Your Business</p>
        <p className="mt-1 text-[9px] uppercase tracking-wider text-zinc-500">Sponsored</p>
      </div>
    </div>
    <div className="flex h-24 items-center justify-center bg-gradient-to-br from-purple-700 via-purple-600 to-fuchsia-600 px-4">
      <p className="text-center font-display text-sm font-extrabold tracking-tight text-white">
        Your offer. Their feed.
      </p>
    </div>
    <div className="flex items-center justify-between px-3.5 py-2.5">
      <p className="text-[10px] font-bold text-white">Book this week</p>
      <span className="flex items-center gap-1 rounded-md bg-white/10 px-2 py-1 text-[9px] font-bold text-white">
        Get Quote <MousePointerClick className="h-2.5 w-2.5" />
      </span>
    </div>
  </div>
);

const GoogleCard = () => (
  <div
    data-testid="hero-google-card"
    className="w-[240px] rounded-2xl border border-white/10 bg-[#101015] p-3.5 shadow-xl shadow-black/50"
  >
    <div className="flex items-center gap-2.5 rounded-full border border-white/10 bg-white/5 px-3.5 py-2">
      <Search className="h-3.5 w-3.5 shrink-0 text-zinc-500" />
      <p className="truncate text-xs text-zinc-300">best [service] near me</p>
    </div>
    <div className="mt-3 border-l-2 border-purple-500 pl-3">
      <p className="text-[9px] font-bold uppercase tracking-wider text-zinc-500">Sponsored</p>
      <p className="mt-0.5 text-xs font-bold text-purple-300">Your Business — Call Today</p>
      <p className="mt-0.5 text-[10px] text-zinc-500">Free quotes • Fast response</p>
    </div>
  </div>
);

export const Hero = () => (
  <section id="home" className="relative overflow-hidden pt-28 pb-14 sm:pt-32 lg:pt-36 lg:pb-20">
    <div className="mx-auto grid max-w-7xl grid-cols-1 items-center gap-16 px-5 sm:px-8 lg:grid-cols-12">
      <div className="lg:col-span-7">
        <motion.div
          initial={{ opacity: 0, y: 12 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.7, delay: 0.3 }}
        >
          <Eyebrow>Meta + Google Advertising For Growing Businesses</Eyebrow>
        </motion.div>

        <h1 className="mt-9 font-display text-5xl font-extrabold leading-[1.02] tracking-tight text-white sm:text-6xl lg:text-7xl">
          <MaskedLine delay={0.4}>WE HELP BUSINESSES</MaskedLine>
          <MaskedLine delay={0.55}>
            GET <span className="text-purple-500">MORE CUSTOMERS.</span>
          </MaskedLine>
        </h1>

        <motion.p
          initial={{ opacity: 0, y: 16 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.7, delay: 0.95 }}
          className="mt-6 max-w-xl font-display text-xl font-bold leading-snug tracking-tight text-zinc-200 md:text-2xl"
        >
          Your next customers are already online. We help them find you.
        </motion.p>

        <motion.p
          initial={{ opacity: 0, y: 16 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.7, delay: 1.1 }}
          className="mt-4 max-w-xl text-base font-medium leading-relaxed text-zinc-400"
        >
          Viral Pulse runs Meta and Google ad campaigns designed to reach the
          right people, generate better leads, and grow your business.
        </motion.p>

        <motion.div
          initial={{ opacity: 0, y: 16 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.7, delay: 1.25 }}
          className="mt-8"
        >
          <button
            type="button"
            data-testid="hero-audit-cta"
            onClick={goToAudit}
            className="group flex w-full items-center justify-center gap-2.5 rounded-full bg-purple-600 px-10 py-5 text-base font-bold text-white shadow-[0_10px_40px_-10px_rgba(139,92,246,0.6)] transition-all duration-300 hover:bg-purple-500 hover:shadow-[0_14px_50px_-10px_rgba(139,92,246,0.75)] sm:w-auto"
          >
            GET MY FREE MARKETING AUDIT
            <ArrowUpRight className="h-5 w-5 transition-transform duration-300 group-hover:translate-x-1 group-hover:-translate-y-1" />
          </button>
          <p className="mt-4 max-w-md text-sm text-zinc-500">
            See where you may be missing customers — and what we'd do differently.
          </p>
        </motion.div>
      </div>

      <div className="relative flex justify-center lg:col-span-5 lg:justify-end">
        <motion.div
          initial={{ opacity: 0, y: 32 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.9, delay: 0.9, ease: [0.16, 1, 0.3, 1] }}
          className="relative"
        >
          <PhoneCard />
          <motion.div
            animate={{ y: [0, -8, 0] }}
            transition={{ duration: 6, repeat: Infinity, ease: "easeInOut" }}
            className="absolute -left-28 -top-14 hidden sm:block lg:-left-44"
          >
            <MetaAdCard />
          </motion.div>
          <motion.div
            animate={{ y: [0, 8, 0] }}
            transition={{ duration: 7, repeat: Infinity, ease: "easeInOut", delay: 1 }}
            className="absolute -bottom-12 -right-8 hidden sm:block lg:-right-20"
          >
            <GoogleCard />
          </motion.div>
        </motion.div>
      </div>
    </div>
  </section>
);
